import "../tree/node.js";
import { describeHandler } from "../inspector.js";
import { activationHandler } from "./activate.js";
import { composeHandlers } from "./composed.js";
import { focusHandler } from "./focus.js";
//#region src/handler/handler.ts
/**
* Runs `action` through the handler chain starting at `nodeId`.
*
* Call `next()` to stop at the current node, `next(parentId)` to delegate to another node
* (optionally with a different action).
*/
function executeHandler(node, action) {
	const next = (id, newAction) => {
		if (id == null) return null;
		const nextNode = node.tree.nodes.get(id);
		if (nextNode == null || !nextNode.connected) return null;
		return executeHandler(nextNode, newAction ?? action);
	};
	return node.handler(action, {
		node,
		next
	});
}
/**
* Primitive navigation handler that passes all actions to its parent node.
* Query action is an exception and is not passed to parent nodes.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#parent-handler}
* @see {@link NavigationHandler}
*/
function parentHandler(action, ctx) {
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeHandler(action, { name: "parent" });
	if (action.kind === "query") return null;
	if (ctx.node.parent !== null) return ctx.next(ctx.node.parent);
	return ctx.next();
}
/**
* Basic composed navigation handler for nodes that can be focused. When not extended tt doesn't respond to any actions and passes them to parent.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#default-handler}
* @see {@link ComposedHandler}
*/
const defaultHandler = composeHandlers([focusHandler(), parentHandler]);
/**
* Basic composed navigation handler similar to {@link defaultHandler}. It differs in that only allows node children to be focused but not the node itself.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#container-handler}
* @see {@link ComposedHandler}
*/
const containerHandler = composeHandlers([focusHandler({ focusWhenEmpty: false }), parentHandler]);
/**
* If `onActivate` is provided, composes `activationHandler(onActivate)` onto `defaultHandler`;
* otherwise returns `defaultHandler`.
*
* @see {@link ActivateCallback} for callback function that is called when node receives activate action.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#item-handler}
* @see {@link ComposedHandler}
*/
const itemHandler = (onActivate) => {
	if (onActivate == null) return defaultHandler;
	return defaultHandler.compose(activationHandler(onActivate));
};
//#endregion
export { containerHandler, defaultHandler, executeHandler, itemHandler, parentHandler };

//# sourceMappingURL=handler.js.map